import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { TimePickerComponent } from '../../../shared/components/time-picker/time-picker.component';

import { format, differenceInMilliseconds, parseISO } from 'date-fns';
import { computeMinutesFromRange, formatTimeWorked } from '../../invoice/shared/invoice-utils';

import { ChangeDetectorRef } from '@angular/core';
import { AppointmentCheckService } from '../../../services/appointment-check.service';
import { DateUtilsService } from '../../../services/date-utils.service';
import {
  SelectedVisitDetails,
  ActivityDetails,
  AppointmentDetail,
  TaskDetail,
  HospitalisationType,
  AppointmentTaskUpdatePayload
} from '../../../shared/models/appointment-check.model';
import { ApiResponseModel } from '../../../shared/models/result.model';
import { of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { DynamicTextPipe } from '../../../core/pipes/dynamic-text.pipe';
import { DateWithTimezonePipe } from '../../../core/pipes/dateWithTimezone.pipe';

@Component({
  selector: 'app-edit-check-in-out-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    ProgressSpinnerModule,
    CheckboxModule,
    TextareaModule,
    SelectModule,
    ToastModule,
    TimePickerComponent,
    DynamicTextPipe
  ],
  templateUrl: './edit-check-in-out-modal.component.html',
  styleUrls: ['./edit-check-in-out-modal.component.scss'],
  providers: [MessageService, DatePipe] // DatePipe for formatting if needed elsewhere
})
export class EditCheckInOutModalComponent implements OnInit, OnChanges {
  // #region Properties
  // #region Inputs & Outputs
  @Input() displayModal: boolean = false;
  @Input() currentVisitDetails: SelectedVisitDetails | null = null;
  @Output() modalClosed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  // #endregion

  // #region Injected Services
  private appointmentCheckService = inject(AppointmentCheckService);
  private messageService = inject(MessageService);
  private fb = inject(FormBuilder);
  private readonly dateUtilsService = inject(DateUtilsService);
  private cdRef = inject(ChangeDetectorRef);
  // #endregion

  // #region State Properties
  isLoading: boolean = false;
  isUpdating: boolean = false;
  activityDetails: ActivityDetails | null = null;
  appointmentDetails: AppointmentDetail | null = null;
  monthlyTasks: TaskDetail[] = [];
  otherTasks: TaskDetail[] = [];
  hospitalizationTypes: HospitalisationType[] = [];
  totalTime: string = '';
  maxTimeDisplay: string = '0h 0m';
  isOverTime: boolean = false;
  isCheckoutBeforeCheckin: boolean = false;
  combinedCheckInTime: Date | null = null;
  combinedCheckOutTime: Date | null = null;
  checkInOriginalDate: string = '';
  checkOutOriginalDate: string = '';
  private apiCallsInProgress = 0;
  // #endregion

  // #region Form Properties
  checkInOutForm!: FormGroup;
  taskControls: { [key: string]: boolean } = {};
  // #endregion

  // #region Computed Properties (Getters)
  get caregiverName(): string {
    return this.appointmentDetails?.careGiverName || this.currentVisitDetails?.caregiverName || '';
  }

  get caregiverPhoneNumber(): string {
    return this.appointmentDetails?.careGiverPhoneNo || '';
  }

  get isActionButtonDisabled(): boolean {
    const isHospitalized = this.checkInOutForm.get('isHospitalized')?.value;
    const hospitalizationTypeSkey = this.checkInOutForm.get('hospitalizationTypeSkey')?.value;
    const hospitalizationReason = this.checkInOutForm.get('hospitalizationReason')?.value;

    if (isHospitalized && !hospitalizationTypeSkey) {
      return true;
    }
    if (isHospitalized && this.isOtherHospitalizationTypeSelected() && !hospitalizationReason?.trim()) {
      return true;
    }
    return false;
  }
  // #endregion
  // #endregion

  // #region Lifecycle Hooks
  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentVisitDetails'] && this.currentVisitDetails && this.displayModal) {
      this.loadActivityDetails();
      this.loadAppointmentDetails();
      if (this.hospitalizationTypes.length === 0) {
        this.loadHospitalizationTypes();
      }
    }
    if (changes['displayModal'] && !this.displayModal) {
      this.resetModal();
    }
  }
  // #endregion

  // #region Form Management
  private initForm(): void {
    this.checkInOutForm = this.fb.group({
      checkInTime: [null],
      checkOutTime: [null],
      isHospitalized: [false],
      hospitalizationTypeSkey: [''],
      hospitalizationReason: [''],
      taskNote: ['']
    });

    this.checkInOutForm.valueChanges.subscribe(() => {
      this.updateCombinedDateTime();
      this.calculateTotalTime();
    });
  }

  private updateFormValues(): void {
    if (!this.activityDetails) return;

    this.updateFormValidators();

    if (this.activityDetails.checkedInTime) {
      const checkInDate = this.dateUtilsService.getTimezoneDate(this.activityDetails.checkedInTime);
      this.checkInOriginalDate = format(checkInDate, 'yyyy-MM-dd');
      this.checkInOutForm.patchValue({ checkInTime: checkInDate }, { emitEvent: false });
    }

    if (this.activityDetails.checkedOutTime) {
      const checkOutDate = this.dateUtilsService.getTimezoneDate(this.activityDetails.checkedOutTime);
      this.checkOutOriginalDate = format(checkOutDate, 'yyyy-MM-dd');
      this.checkInOutForm.patchValue({ checkOutTime: checkOutDate }, { emitEvent: false });
    }

    this.checkInOutForm.patchValue(
      {
        isHospitalized: this.activityDetails.isHospitalised || false,
        hospitalizationTypeSkey: this.activityDetails.hospitalisationTypeSkey || '',
        hospitalizationReason: this.activityDetails.hospitalisationReason || '',
        taskNote: this.activityDetails.taskNote || ''
      },
      { emitEvent: false }
    );

    this.calculateTotalTime();
    this.cdRef.detectChanges();
  }

  private updateFormValidators(): void {
    if (!this.activityDetails) return;

    const checkInControl = this.checkInOutForm.get('checkInTime');
    const checkOutControl = this.checkInOutForm.get('checkOutTime');

    if (this.activityDetails.isCheckedIn && this.activityDetails.checkedInTime) {
      checkInControl?.setValidators([Validators.required]);
    } else {
      checkInControl?.clearValidators();
    }

    if (this.activityDetails.isCheckedOut && this.activityDetails.checkedOutTime) {
      checkOutControl?.setValidators([Validators.required]);
    } else {
      checkOutControl?.clearValidators();
    }

    checkInControl?.updateValueAndValidity();
    checkOutControl?.updateValueAndValidity();
  }

  private resetModal(): void {
    this.activityDetails = null;
    this.appointmentDetails = null;
    this.monthlyTasks = [];
    this.otherTasks = [];

    if (this.checkInOutForm) {
      const resetValues = {
        isHospitalized: false,
        hospitalizationTypeSkey: '',
        hospitalizationReason: '',
        taskNote: ''
      };

      Object.keys(this.taskControls).forEach((controlName) => {
        if (this.checkInOutForm.contains(controlName)) {
          this.checkInOutForm.removeControl(controlName);
        }
      });
      this.taskControls = {};

      this.checkInOutForm.get('checkInTime')?.clearValidators();
      this.checkInOutForm.get('checkOutTime')?.clearValidators();
      this.checkInOutForm.get('checkInTime')?.updateValueAndValidity();
      this.checkInOutForm.get('checkOutTime')?.updateValueAndValidity();
      this.checkInOutForm.reset(resetValues, { emitEvent: false });
    }

    this.totalTime = '';
    this.maxTimeDisplay = '0h 0m';
    this.isOverTime = false;
    this.isCheckoutBeforeCheckin = false;
    this.isLoading = false;
    this.isUpdating = false;
  }
  // #endregion

  // #region Data Loading & API Calls
  private startApiCall(): void {
    if (this.apiCallsInProgress === 0) {
      this.isLoading = true;
    }
    this.apiCallsInProgress++;
  }

  private completeApiCall(): void {
    this.apiCallsInProgress--;
    if (this.apiCallsInProgress === 0) {
      this.isLoading = false;
    }
  }

  private loadActivityDetails(): void {
    if (!this.currentVisitDetails) return;
    this.startApiCall();
    const dateToSend = this.getDateWithStartTime(this.currentVisitDetails);

    this.appointmentCheckService
      .getActivityByAppointmentIdAndDate(this.currentVisitDetails.appointmentId, dateToSend)
      .subscribe({
        next: (response: ApiResponseModel<ActivityDetails>) => {
          if (response.isSuccess && response.data) {
            this.activityDetails = response.data;
          } else {
            this.activityDetails = {
              isCheckedIn: false,
              isCheckedOut: false,
              checkedInTime: null,
              checkedOutTime: null
            } as ActivityDetails;
          }
          this.maxTimeDisplay = formatTimeWorked(Math.round((this.activityDetails?.maxHours ?? 0) * 60));
          this.updateFormValues();
          this.calculateTotalTime();
          this.completeApiCall();
        },
        error: (err) => {
          if (err.status >= 500) {
            this.showError('Server error loading appointment details. Please try again later.');
          }
          this.activityDetails = {
            isCheckedIn: false,
            isCheckedOut: false,
            checkedInTime: null,
            checkedOutTime: null
          } as ActivityDetails;
          this.maxTimeDisplay = formatTimeWorked(Math.round((this.activityDetails?.maxHours ?? 0) * 60));
          this.updateFormValues();
          this.calculateTotalTime();
          this.completeApiCall();
        }
      });
  }

  private loadAppointmentDetails(): void {
    if (!this.currentVisitDetails) return;
    this.startApiCall();
    const dateToSend = this.getDateWithStartTime(this.currentVisitDetails);

    this.appointmentCheckService.getAppointmentDetail(this.currentVisitDetails.appointmentId, dateToSend).subscribe({
      next: (response: ApiResponseModel<AppointmentDetail>) => {
        if (response.isSuccess && response.data) {
          this.appointmentDetails = response.data;
          this.processTasks();
          if (this.appointmentDetails?.caregiverCheckInCheckOut) {
            this.checkInOutForm.patchValue({
              isHospitalized: this.appointmentDetails.caregiverCheckInCheckOut.isHospitalised || false,
              hospitalizationTypeSkey: this.appointmentDetails.caregiverCheckInCheckOut.hospitalisationTypeSkey || '',
              hospitalizationReason: this.appointmentDetails.caregiverCheckInCheckOut.hospitalisationReason || '',
              taskNote: this.appointmentDetails.caregiverCheckInCheckOut.taskNote || ''
            });
          }
        } else {
        }
        this.completeApiCall();
      },
      error: (err) => {
        this.showError('Error loading task details.');
        this.completeApiCall();
      }
    });
  }

  private loadHospitalizationTypes(): void {
    this.startApiCall();
    this.appointmentCheckService.getHospitalisationTypes().subscribe({
      next: (response: ApiResponseModel<HospitalisationType[]>) => {
        if (response.isSuccess && response.data) {
          this.hospitalizationTypes = response.data;
        } else {
        }
        this.completeApiCall();
      },
      error: (err) => {
        this.completeApiCall();
      }
    });
  }

  refreshHospitalizationTypes(): void {
    this.loadHospitalizationTypes();
  }
  // #endregion

  // #region Event Handlers
  onUpdateTime(): void {
    if (
      !this.currentVisitDetails ||
      !this.activityDetails ||
      this.checkInOutForm.invalid ||
      this.isCheckoutBeforeCheckin
    ) {
      this.showError('Invalid data or form.');
      return;
    }
    this.isUpdating = true;

    const formValues = this.checkInOutForm.value;
    const newCheckInTimeISO = this.combinedCheckInTime
      ? this.dateUtilsService.getUtcWithTimezone(this.combinedCheckInTime)
      : null;
    const newCheckOutTimeISO = this.combinedCheckOutTime
      ? this.dateUtilsService.getUtcWithTimezone(this.combinedCheckOutTime)
      : null;
    const originalCheckIn: string | null = this.activityDetails.checkedInTime ?? null;
    const originalCheckOut: string | null = this.activityDetails.checkedOutTime ?? null;

    const checkInPayload =
      this.activityDetails.isCheckedIn && newCheckInTimeISO
        ? {
            appointmentId: this.currentVisitDetails.appointmentId,
            previousDateTime: originalCheckIn || '',
            updatedDateTime: newCheckInTimeISO,
            isHospitalised: formValues.isHospitalized || false,
            hospitalisationTypeSkey: formValues.hospitalizationTypeSkey || '',
            hospitalisationReason: formValues.hospitalizationReason || '',
            taskNote: formValues.taskNote || ''
          }
        : null;

    const checkOutPayload =
      this.activityDetails.isCheckedOut && newCheckOutTimeISO
        ? {
            appointmentId: this.currentVisitDetails.appointmentId,
            previousDateTime: originalCheckOut || newCheckOutTimeISO,
            updatedDateTime: newCheckOutTimeISO
          }
        : null;

    const finalize = (
      checkInResult?: ApiResponseModel<boolean> | null,
      checkOutResult?: ApiResponseModel<boolean> | null
    ) => {
      this.isUpdating = false;
      const success = (checkInResult && checkInResult.isSuccess) || (checkOutResult && checkOutResult.isSuccess);

      if (checkInPayload && checkOutPayload) {
        if (checkInResult?.isSuccess && checkOutResult?.isSuccess) {
          this.showSuccess('Successfully updated check-in and check-out times.');
        } else {
          if (checkInResult && !checkInResult.isSuccess)
            this.showError(`Check-in update failed: ${checkInResult.message || 'Unknown error'}`);
          if (checkOutResult && !checkOutResult.isSuccess)
            this.showError(`Check-out update failed: ${checkOutResult.message || 'Unknown error'}`);
        }
      } else if (checkInPayload) {
        if (checkInResult?.isSuccess) this.showSuccess('Check-in updated successfully.');
        else this.showError(`Check-in update failed: ${checkInResult?.message || 'Unknown error'}`);
      } else if (checkOutPayload) {
        if (checkOutResult?.isSuccess) this.showSuccess('Check-out updated successfully.');
        else this.showError(`Check-out update failed: ${checkOutResult?.message || 'Unknown error'}`);
      }

      if (success) {
        this.saved.emit();
        this.closeModal();
      }
    };

    if (checkInPayload && checkOutPayload) {
      this.appointmentCheckService
        .updateCheckIn(checkInPayload)
        .pipe(
          switchMap((checkInRes) => {
            if (!checkInRes.isSuccess) {
              return of({ checkInRes, checkOutRes: null });
            }
            return this.appointmentCheckService
              .updateCheckOut(checkOutPayload)
              .pipe(map((checkOutRes) => ({ checkInRes, checkOutRes })));
          }),
          catchError((err) => {
            const errorResponse = {
              isSuccess: false,
              message: 'Error updating.',
              data: false,
              messageDisplayType: 0,
              errors: null,
              innerExceptions: null
            };
            return of({ checkInRes: errorResponse, checkOutRes: null });
          })
        )
        .subscribe(({ checkInRes, checkOutRes }) => {
          finalize(checkInRes, checkOutRes);
        });
    } else if (checkInPayload) {
      this.appointmentCheckService.updateCheckIn(checkInPayload).subscribe({
        next: (res) => finalize(res),
        error: () =>
          finalize({
            isSuccess: false,
            message: 'Error updating check-in.',
            data: false,
            messageDisplayType: 0,
            errors: null,
            innerExceptions: null
          })
      });
    } else if (checkOutPayload) {
      this.appointmentCheckService.updateCheckOut(checkOutPayload).subscribe({
        next: (res) => finalize(null, res),
        error: () =>
          finalize(null, {
            isSuccess: false,
            message: 'Error updating check-out.',
            data: false,
            messageDisplayType: 0,
            errors: null,
            innerExceptions: null
          })
      });
    } else {
      this.isUpdating = false;
      this.showError('No valid times to update.');
    }
  }

  onForceCheckIn(): void {
    if (!this.currentVisitDetails) return;
    this.closeModal();

    const formValues = this.checkInOutForm.value;
    const payload = {
      appointmentId: this.currentVisitDetails.appointmentId,
      date: this.getDateWithStartTime(this.currentVisitDetails),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      taskNote: formValues.taskNote || '',
      isHospitalised: formValues.isHospitalized || false,
      hospitalisationTypeSkey: formValues.hospitalizationTypeSkey || '',
      hospitalisationReason: formValues.hospitalizationReason || ''
    };

    this.appointmentCheckService.checkInForce(payload).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.showSuccess('Successfully forced clock-in.');
          this.saved.emit();
        } else {
          this.showError(res.message || 'Failed to force clock-in.');
        }
      },
      error: (err) => {
        this.showError('Error forcing clock-in.');
      }
    });
  }

  onForceCheckOut(): void {
    if (!this.currentVisitDetails) return;
    this.closeModal();

    const formValues = this.checkInOutForm.value;
    const payload = {
      appointmentId: this.currentVisitDetails.appointmentId,
      date: this.getDateWithStartTime(this.currentVisitDetails),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      taskNote: formValues.taskNote || '',
      isHospitalised: formValues.isHospitalized || false,
      hospitalisationTypeSkey: formValues.hospitalizationTypeSkey || '',
      hospitalisationReason: formValues.hospitalizationReason || ''
    };

    this.appointmentCheckService.checkOutForce(payload).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.showSuccess('Successfully forced clock-out.');
          this.saved.emit();
        } else {
          this.showError(res.message || 'Failed to force clock-out.');
        }
      },
      error: (err) => {
        this.showError('Error forcing clock-out.');
      }
    });
  }

  onTaskCheckboxChange(taskSkey: string): void {
    const controlName = `task_${taskSkey}`;
    const isChecked = this.checkInOutForm.get(controlName)?.value;
    const task = this.findTask(taskSkey);

    if (!task || !this.currentVisitDetails || !this.appointmentDetails?.sKey) return;

    task.isCompleted = isChecked;
    const payload: AppointmentTaskUpdatePayload = {
      taskSKey: taskSkey,
      appointmentSKey: this.appointmentDetails.sKey,
      isComplete: isChecked,
      date: this.getDateWithStartTime(this.currentVisitDetails),
      taskNote: this.checkInOutForm.get('taskNote')?.value || ''
    };

    this.appointmentCheckService.updateAppointmentTask(payload).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.showSuccess('Task status updated successfully.');
        } else {
          this.revertTaskStatus(controlName, task, isChecked);
          this.showError(response.message || 'Failed to update task status');
        }
      },
      error: (error) => {
        this.revertTaskStatus(controlName, task, isChecked);
        this.showError('Failed to update task status. Please try again.');
      }
    });
  }

  onHospitalizedChange(): void {
    const isHospitalized = this.checkInOutForm.get('isHospitalized')?.value;

    if (isHospitalized) {
      this.checkInOutForm.patchValue({ taskNote: '' });
      this.resetAllTasks();
    } else {
      this.checkInOutForm.patchValue({
        hospitalizationTypeSkey: '',
        hospitalizationReason: ''
      });
    }
  }

  onHospitalizationTypeChange(): void {
    if (!this.isOtherHospitalizationTypeSelected()) {
      this.checkInOutForm.patchValue({ hospitalizationReason: '' });
    }
  }
  // #endregion

  // #region UI & Helper Methods
  closeModal(): void {
    this.displayModal = false;
    this.modalClosed.emit();
    this.resetModal();
  }

  getFormattedDate(dateString: string): string {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  }

  getFormattedTime(timeString: string): string {
    if (!timeString) return '';
    try {
      const date = parseISO(timeString);
      return this.dateUtilsService.formatWithTimezone(date, 'hh:mm a');
    } catch {
      return timeString;
    }
  }

  getScheduledStartTime(): string {
    const timeToFormat = this.appointmentDetails?.taskStartTime || this.currentVisitDetails?.start || '';
    return this.getFormattedTime(timeToFormat);
  }

  getScheduledEndTime(): string {
    if (this.appointmentDetails?.taskStartTime && typeof this.appointmentDetails.maxHours === 'number') {
      try {
        const startDate = new Date(this.appointmentDetails.taskStartTime);
        if (!isNaN(startDate.getTime())) {
          if (this.appointmentDetails.maxHours === 0) {
            // If maxHours is 0, scheduled end time is same as start time
            return this.dateUtilsService.formatWithTimezone(startDate, 'hh:mm a');
          }
          const maxMinutes = this.appointmentDetails.maxHours * 60;
          const endDate = new Date(startDate.getTime() + maxMinutes * 60000);
          return this.dateUtilsService.formatWithTimezone(endDate, 'hh:mm a');
        }
      } catch {}
    }
    return this.getFormattedTime(this.currentVisitDetails?.end || '');
  }

  isOtherHospitalizationTypeSelected(): boolean {
    const selectedSkey = this.checkInOutForm.get('hospitalizationTypeSkey')?.value;
    if (!selectedSkey) return false;
    const otherType = this.hospitalizationTypes.find(
      (type) => type.name.toLowerCase() === 'other' && type.sKey === selectedSkey
    );
    return !!otherType;
  }

  private showSuccess(message: string): void {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
  }

  private showError(message: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }
  // #endregion

  // #region Private Logic
  private processTasks(): void {
    if (!this.appointmentDetails?.taskDetailsList) return;

    this.monthlyTasks = [];
    this.otherTasks = [];

    this.appointmentDetails.taskDetailsList.forEach((task) => {
      if (task.isMonthly) this.monthlyTasks.push(task);
      else this.otherTasks.push(task);

      const controlName = `task_${task.taskSkey}`;
      this.taskControls[controlName] = task.isCompleted;
      this.checkInOutForm.addControl(controlName, this.fb.control(task.isCompleted));
    });
  }

  private findTask(taskSkey: string): TaskDetail | undefined {
    return (
      this.monthlyTasks.find((t) => t.taskSkey === taskSkey) || this.otherTasks.find((t) => t.taskSkey === taskSkey)
    );
  }

  private revertTaskStatus(controlName: string, task: TaskDetail, originalIsChecked: boolean): void {
    this.checkInOutForm.get(controlName)?.setValue(!originalIsChecked, { emitEvent: false });
    task.isCompleted = !originalIsChecked;
  }

  private resetAllTasks(): void {
    if (!this.appointmentDetails?.taskDetailsList) return;

    this.appointmentDetails.taskDetailsList.forEach((task) => {
      const controlName = `task_${task.taskSkey}`;
      const wasCompleted = this.checkInOutForm.get(controlName)?.value === true;

      if (this.checkInOutForm.contains(controlName)) {
        this.checkInOutForm.get(controlName)?.setValue(false, { emitEvent: false });
      }
      task.isCompleted = false;

      if (wasCompleted && this.currentVisitDetails && this.appointmentDetails?.sKey) {
        const payload: AppointmentTaskUpdatePayload = {
          taskSKey: task.taskSkey,
          appointmentSKey: this.appointmentDetails.sKey,
          isComplete: false,
          date: this.getDateWithStartTime(this.currentVisitDetails),
          taskNote: ''
        };
        this.appointmentCheckService.updateAppointmentTask(payload).subscribe({
          error: (error) => {
            this.showError('Failed to update task status for hospitalization.');
          }
        });
      }
    });
  }

  private updateCombinedDateTime(): void {
    const formValues = this.checkInOutForm.value;

    if (formValues.checkInTime && this.activityDetails?.checkedInTime) {
      const originalDate = this.dateUtilsService.getTimezoneDate(this.activityDetails.checkedInTime);
      const newTime = new Date(formValues.checkInTime);
      this.combinedCheckInTime = new Date(
        originalDate.getFullYear(),
        originalDate.getMonth(),
        originalDate.getDate(),
        newTime.getHours(),
        newTime.getMinutes()
      );
    }

    if (formValues.checkOutTime) {
      let baseDate: Date;
      if (this.activityDetails?.checkedOutTime) {
        baseDate = this.dateUtilsService.getTimezoneDate(this.activityDetails.checkedOutTime);
      } else if (this.activityDetails?.checkedInTime) {
        baseDate = this.dateUtilsService.getTimezoneDate(this.activityDetails.checkedInTime);
      } else if (this.currentVisitDetails?.date) {
        baseDate = new Date(this.currentVisitDetails.date);
      } else {
        baseDate = new Date();
      }
      const newTime = new Date(formValues.checkOutTime);
      this.combinedCheckOutTime = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        newTime.getHours(),
        newTime.getMinutes()
      );
    }
  }

  calculateTotalTime(): void {
    if (!this.activityDetails || !this.checkInOutForm) {
      this.totalTime = '';
      return;
    }
    const checkInTime = this.checkInOutForm.get('checkInTime')?.value as Date | null;
    const checkOutTime = this.checkInOutForm.get('checkOutTime')?.value as Date | null;
    if (checkInTime && checkOutTime && this.activityDetails.isCheckedIn) {
      this.updateCombinedDateTime();
      if (!this.combinedCheckInTime || !this.combinedCheckOutTime) {
        this.totalTime = '';
        return;
      }
      const minutes = computeMinutesFromRange(this.combinedCheckInTime, this.combinedCheckOutTime);
      if (minutes <= 0) {
        this.isCheckoutBeforeCheckin = true;
        this.totalTime = 'Invalid time range';
        this.isOverTime = false;
        return;
      }
      this.isCheckoutBeforeCheckin = false;
      this.totalTime = formatTimeWorked(minutes);
      if (this.activityDetails.maxHours) {
        this.isOverTime = minutes > this.activityDetails.maxHours * 60;
      } else {
        this.isOverTime = false;
      }
    } else {
      this.totalTime = '';
      this.isCheckoutBeforeCheckin = false;
      this.isOverTime = false;
    }
  }

  private getDateWithStartTime(selectedTaskData: SelectedVisitDetails): string {
    let dateToSend = selectedTaskData.date;
    if (selectedTaskData.start) {
      const startDateTimeUTC = new Date(selectedTaskData.start);
      const startDateTimeCompany = this.dateUtilsService.getTimezoneDate(startDateTimeUTC);

      // Parse the date string correctly to avoid timezone issues
      // selectedTaskData.date is in format "YYYY-MM-DD"
      const dateParts = selectedTaskData.date.split('-').map((part) => parseInt(part, 10));
      const localDateTime = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]); // month is 0-indexed

      localDateTime.setHours(
        startDateTimeCompany.getHours(),
        startDateTimeCompany.getMinutes(),
        startDateTimeCompany.getSeconds(),
        startDateTimeCompany.getMilliseconds()
      );
      dateToSend = this.dateUtilsService.getUtcWithTimezone(localDateTime);
      console.log('Date with start time:', dateToSend);
    }
    return dateToSend;
  }

  // #endregion
}
